import * as yup from 'yup'

export const referralSchema = yup.object().shape({
  patientHistory: yup.string().required('Patient history is required'),
  speciality: yup.object().nullable().required('Specialty is required'),
  urgency: yup.string().required('Urgency is required'),
  attachments: yup.mixed().nullable(),
  referralDoctorName: yup.string().optional(),
  referralHospitalName: yup.string().optional(),
})

export const patientSchema = yup.object().shape({
  name: yup
    .string()
    .min(4, 'Name must be at least 4 characters long')
    .required('Required'),
  gender: yup.string().oneOf(['Male', 'Female', 'Other']).required('Required'),
  dob: yup.string().required('Required').nullable(),
  phone: yup
    .string()
    .matches(
      /^\+[0-9]{10,}$/g,
      'Phone number must start with "+" and only contain numbers',
    )
    .required('Required'),
  address: yup.string().required('Required'),
})

export const warningSchema = yup.object().shape({
  warning: yup
    .string()
    .min(4, 'Warning must be at least 4 characters long')
    .required('Required'),
})

export const emailSchema = yup.object().shape({
  email: yup.string().email('Invalid Email').required('Required'),
  subject: yup.string().required('Required'),
  body: yup
    .string()
    .min(4, 'Body must be at least 4 characters long')
    .required('Required'),
})

export const smsSchema = yup.object().shape({
  phone: yup
    .string()
    .matches(
      /^\+[0-9]{10,}$/g,
      'Phone number must start with "+" and only contain numbers',
    )
    .required('Required'),
  body: yup
    .string()
    .min(4, 'Body must be at least 4 characters long')
    .required('Required'),
})

const URL =
  /^((https?|ftp):\/\/)?(www.)?(((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:)*@)?(((\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5])\.(\d|[1-9]\d|1\d\d|2[0-4]\d|25[0-5]))|((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.?)(:\d*)?)(\/((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)+(\/(([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)*)*)?)?(\?((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|[\uE000-\uF8FF]|\/|\?)*)?(\#((([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(%[\da-f]{2})|[!\$&'\(\)\*\+,;=]|:|@)|\/|\?)*)?$/i

export const urlSchema = yup.object().shape({
  url: yup.string().matches(URL, 'Enter a valid url'),
})

export const soapNotesSchema = yup.object().shape({
  findings: yup.string().required('Required'),
  assessment: yup.string().required('Required'),
  plan: yup.string().required('Required'),
  symptoms: yup.string().required('Required'),
  interactionType: yup
    .string()
    .oneOf([
      // 'audio_call',
      // 'video_call',
      // 'chat',
      // 'video_declined',
      'medical_consult',
      'administrative_misc',
      'inactive_chat',
    ])
    .required('Required *'),
})
export const soapNotesSchemaMH = yup.object().shape({
  findings: yup.string().required('Required'),
  assessment: yup.string().required('Required'),
  plan: yup.string().required('Required'),
  symptoms: yup.string().required('Required'),
  interactionType: yup
    .string()
    .oneOf([
      'audio_call',
      'video_call',
      'chat',
      'video_declined',
      'inactive_chat',
    ])
    .required('Required *'),
})
export const soapNotesWithLabSchema = yup.object().shape({
  findings: yup.string().required('Required'),
  assessment: yup.string().required('Required'),
  plan: yup.string().required('Required'),
  symptoms: yup.string().required('Required'),
  activityType: yup.string().oneOf(['open', 'close']).required('Required *'),
  statusType: yup
    .string()
    .oneOf(['normal', 'abnormal', 'critical', 'undetermined', 'pending'])
    .required('Required *'),
})

export const medicalNotesSchema = yup.object().shape({
  medicalNotes: yup.string().required('Select at least one option'),
  other: yup.string().when('medicalNotes', {
    is: 'other',
    then: yup.string().required('Select at least one option'),
  }),
  fromDate: yup.string().required('Required').nullable(),
  toDate: yup.string().required('Required').nullable(),
  encounterDate: yup.string().required('Required').nullable(),
})

export const physioPrescriptionSchema = yup.object().shape({
  treatmentRecommended: yup.string().required('Required'),
  productsRecommended: yup.string().required('Required'),
  approvalStatus: yup.string().required('Required').nullable(),
  declineReason: yup.string().when('approvalStatus', {
    is: 'declined',
    then: yup.string().required('Please provide a reason for declining'),
    otherwise: yup.string().notRequired(),
  }),
})

export const prescriptionSchema = yup.object().shape({
  allergies: yup.string().required('Required'),
  // medicalCondition: yup.string().required('Required'),
  previousMedications: yup.string().required('Required'),
  // isPaymentRequired: yup.string().required('Required'),
  // reasonOfNotCharging: yup.string().when('isPaymentRequired', {
  //   is: 'No',
  //   then: yup.string().required('Required'),
  // }),
  medications: yup.array().of(
    yup.object().shape({
      name: yup.string().required('Required'),
      // composition: yup.string().required('Required'),
      // route: yup.string().required('Required'),
      // dosage: yup.string().required('Required'),
      // frequency: yup.string().required('Required'),
      // duration: yup.string().required('Required'),
      quantity: yup.string().required('Required'),
      // refills: yup.string().required('Required'),
      instructions: yup.string().required('Required'),
      type: yup.string().required('Required'),
    }),
  ),
  refill: yup.string().required('Required'),
  date: yup.string().required('Required').nullable(),
})

export const labRequisitionSchema = yup.object().shape({
  date: yup.string().required('Required').nullable(),
  reason: yup.string().required('Required'),
  isScreening: yup.string().required('Required'),
  screeningInterval: yup.string().when('isScreening', {
    is: 'Yes',
    then: yup.string().required('Required'),
    otherwise: yup.string().notRequired(),
  }),
})
export const labRequisitionTemplateSchema = yup.object().shape({
  reason: yup.string().required('Required'),
  name: yup.string().required('Required'),
})

export const imagingSchema = (gender, formType) => {
  // CT Scan form validation
  if (formType === 'ctScan') {
    const baseSchema = {
      healthInsuranceCardNumber: yup.string().when('billTo', {
        is: (val) => val === 'Insurance',
        then: yup.string().required('Health Insurance Card Number is required'),
        otherwise: yup.string(),
      }),
      isolationSpecify: yup.string().when('isolationPrecautions', {
        is: (val) => val && val.includes('Yes'),
        then: yup.string().required('Required'),
        otherwise: yup.string(),
      }),
      allergiesSpecify: yup.string().when('allergies', {
        is: (val) => val && val.includes('Yes'),
        then: yup.string().required('Required'),
        otherwise: yup.string(),
      }),
      organTransplantSpecify: yup.string().when('organTransplant', {
        is: (val) => val && val.includes('Yes'),
        then: yup.string().required('Required'),
        otherwise: yup.string(),
      }),
      previousChemotherapySpecify: yup.string().when('previousChemotherapy', {
        is: (val) => val && val.includes('Yes'),
        then: yup.string().required('Required'),
        otherwise: yup.string(),
      }),
      powerCompatibleLineSpecify: yup.string().when('powerCompatibleLine', {
        is: (val) => val && val.includes('Yes'),
        then: yup.string().required('Required'),
        otherwise: yup.string(),
      }),
      mechanicalLiftSpecify: yup.string().when('mechanicalLiftRequired', {
        is: (val) => val && val.includes('Yes'),
        then: yup.string().required('Required'),
        otherwise: yup.string(),
      }),
      vascularDiseaseSpecify: yup.string().when('vascularDisease', {
        is: (val) => val && val.includes('Yes'),
        then: yup.string().required('Required'),
        otherwise: yup.string(),
      }),
      renalDiseaseSpecify: yup.string().when('renalDiseaseOrSolitaryKidney', {
        is: (val) => val && val.includes('Yes'),
        then: yup.string().required('Required'),
        otherwise: yup.string(),
      }),
    };

    if (gender === 'Female' || gender?.toLowerCase() === 'female') {
      return yup.object().shape({
        ...baseSchema,
        lmpDate: yup.string().when('pregnant', {
          is: (val) => val && val.includes('Yes'),
          then: yup.string().required('Required').nullable(),
          otherwise: yup.string().nullable(),
        }),
      });
    }
    return yup.object().shape(baseSchema);
  }

  // MRI form validation
  if (formType === 'mri') {
    const baseSchema = {
      healthInsuranceCardNumber: yup.string().when('billTo', {
        is: (val) => val === 'Insurance',
        then: yup.string().required('Health Insurance Card Number is required'),
        otherwise: yup.string(),
      }),
      metallicSpecify: yup.string().when('metallicForeignBodyOrImplants', {
        is: (val) => val && val.includes('Yes'),
        then: yup.string().required('Required'),
        otherwise: yup.string(),
      }),
      htnDiabetesSpecify: yup.string().when('hypertensionOrLongStandingInsulinDiabetes', {
        is: (val) => val && val.includes('Yes'),
        then: yup.string().required('Required'),
        otherwise: yup.string(),
      }),
      mechanicalLiftSpecify: yup.string().when('mechanicalLiftRequired', {
        is: (val) => val && val.includes('Yes'),
        then: yup.string().required('Required'),
        otherwise: yup.string(),
      }),
    };

    if (gender === 'Female' || gender?.toLowerCase() === 'female') {
      return yup.object().shape({
        ...baseSchema,
        lmpDate: yup.string().when('pregnant', {
          is: (val) => val && val.includes('Yes'),
          then: yup.string().required('Required').nullable(),
          otherwise: yup.string().nullable(),
        }),
      });
    }
    return yup.object().shape(baseSchema);
  }

  // Lab MRI form validation
  if (formType === 'otherImaging') {
    return yup.object().shape({
      healthInsuranceCardNumber: yup.string().when('billTo', {
        is: (val) => val === 'Insurance',
        then: yup.string().required('Health Insurance Card Number is required'),
        otherwise: yup.string(),
      }),
      examRequested: yup.string().required('Exam Requested is required'),
      reasonHistory: yup.string().required('Reason/History is required'),
      // Keep .required() before .matches(): '' fails both, and abortEarly routes
      // the message by chain order (matches() here does NOT skip empty strings).
      heightCm: yup
        .string()
        .required('Height is required')
        .matches(/^\s*(\d+(\.\d+)?|na|n\/a)\s*$/i, 'Enter a number or NA'),
      weightKg: yup
        .string()
        .required('Weight is required')
        .matches(/^\s*(\d+(\.\d+)?|na|n\/a)\s*$/i, 'Enter a number or NA'),
      infectionConcernsOther: yup.string().when('infectionConcerns', {
        is: (val) => val && val.includes('Other'),
        then: yup.string().required('Required'),
        otherwise: yup.string(),
      }),
      p5DateFollowup: yup.string().when('orderingClinicianTimeframe', {
        is: (val) => val && val.includes('P5'),
        then: yup.string().required('Required').nullable(),
        otherwise: yup.string().nullable(),
      }),
    });
  }

  // For normal/X-ray form - no required fields for intermediate saves
  return yup.object().shape({
    // Fields can be empty for intermediate saves
  })
}
export const imagingSchemaTemplate = (gender) => {
  if (gender === 'Female') {
    return yup.object().shape({
      // lmp: yup.string().required('Required').nullable(),
      // date: yup.string().required('Required').nullable(),
      procedure: yup.string().required('Required'),
      reason: yup.string().required('Required'),
      name: yup.string().required('Required'),
    })
  }
  return yup.object().shape({
    // date: yup.string().required('Required').nullable(),
    procedure: yup.string().required('Required'),
    reason: yup.string().required('Required'),
    name: yup.string().required('Required'),
  })
}
export const healthCardSchema = yup.object().shape({
  healthCardNumber: yup
    .string()
    .required('Required')
    .min(10, 'Health card number must be 10 digits'),
})

export const patientEmailSchema = yup.object().shape({
  patientEmailforDocuments: yup
    .string()
    .email('Enter a valid email')
    .required('Required'),
})

export const prescriptionTemplateSchema = yup.object().shape({
  name: yup.string().required('Required'),
})

export const chatModalSchema = yup.object().shape({
  issue: yup.string().required('Required'),
  message: yup.string().required('Required'),
})
